import { QueryKeys } from "@/constants/Query";
import { useQueryClient } from "@tanstack/react-query";
import { type ParsedLocation, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  PatientTabType,
  type PatientTabsResponse,
  type PatientTabsResponse_Tab,
} from "../network/usePatientTabs";

/**
 * `usePreviewTabs` is a custom React hook designed to manage a frontend-only type of tab called "Preview" patient tabs.
 * Preview tabs are a temporary tab that disappears (or is "hidden") when navigating away from a patient's chart.
 * The idea is that a user clicks on a patient row from the Waiting Room in order to preview a patient's chart to
 * decide if they want to take the patient. If they click away, the tab disappears from the UI. However, if they hit "back"
 * on their browser, the tab is restored.
 *
 * How it works:
 * 1. **Router Event Subscription**: The hook subscribes to TanStack Router's `onResolved` event, which
 *    fires after any navigation (including browser history navigation) is completed.
 *
 * 2. **State Management for tab history**: It maintains an internal state (`hiddenPreviewTabs`)
 *    as an object, where keys are `patientId`s and values are the full `PatientTabsResponse_Tab` data
 *    for "Preview" tabs that this hook has hidden.
 *
 * 3. **Navigating Away from a "Preview" Tab**:
 *    - When the `onResolved` event indicates a navigation *away* from a patient URL (e.g., `/patient/:patientId`),
 *      the hook checks if the tab for this `patientId` in the global React Query cache (`QueryKeys.Provider.PATIENT_TABS`)
 *      is of type `PatientTabType.PREVIEW`.
 *    - If it is, the tab's data is saved into the `hiddenPreviewTabs` state, and the tab is then removed
 *      from the React Query cache, effectively making it disappear from the UI.
 *
 * 4. **Navigating To a Route (Potentially Restoring a Hidden Tab)**:
 *    - When the `onResolved` event indicates navigation *to* a patient URL, the hook checks if the `toPatientId`
 *      exists as a key in its `hiddenPreviewTabs` state.
 *    - If a match is found (meaning this hook previously hid this tab):
 *      - The tab data is retrieved from `hiddenPreviewTabs`.
 *      - It's added back to the React Query cache for `QueryKeys.Provider.PATIENT_TABS` (if not already present,
 *        to avoid duplicates if another process re-added it).
 *      - The entry for this `patientId` is removed from `hiddenPreviewTabs`, as the tab is now restored.
 *
 *
 * NOTE: `usePreviewTabs` is not responsible for adding preview tabs; it only handles the logic of hiding and restoring them.
 * See `useOpenPatientChartPreview` for adding preview tabs.
 */
export const usePreviewTabs = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [hiddenPreviewTabs, setHiddenPreviewTabs] = useState<
    Record<string, PatientTabsResponse_Tab>
  >({});
  // We need a ref to access the latest value of hiddenPreviewTabs in the router subscription
  // without causing the effect to re-run. This is because the router subscription callback
  // is created once when the effect runs, but needs access to the latest state when it executes.
  const hiddenPreviewTabsRef = useRef(hiddenPreviewTabs);

  // Keep the ref in sync with state
  useEffect(() => {
    hiddenPreviewTabsRef.current = hiddenPreviewTabs;
  }, [hiddenPreviewTabs]);

  useEffect(() => {
    const unsubscribe = router.subscribe(
      "onResolved",
      (evt: {
        fromLocation?: ParsedLocation;
        toLocation: ParsedLocation;
        pathChanged: boolean;
      }) => {
        const { fromLocation, toLocation, pathChanged } = evt;

        const fromPath = fromLocation?.pathname;
        const toPath = toLocation.pathname;

        let fromPatientId: string | undefined = undefined;
        if (fromPath) {
          const fromMatch = fromPath.match(/\/patient\/([^\/]+)/);
          fromPatientId = fromMatch?.[1];
        }

        let toPatientId: string | undefined = undefined;
        if (toPath) {
          const toMatch = toPath.match(/\/patient\/([^\/]+)/);
          toPatientId = toMatch?.[1];
        }

        // Hide the preview tab if we're navigating away from a patient chart
        // This includes both:
        // 1. Navigating to a different patient (fromPatientId !== toPatientId)
        // 2. Navigating away from patient chart entirely (fromPatientId exists but toPatientId doesn't)
        if (
          fromPatientId &&
          pathChanged &&
          (!toPatientId || fromPatientId !== toPatientId)
        ) {
          const currentTabs = queryClient.getQueryData<PatientTabsResponse>(
            QueryKeys.Provider.PATIENT_TABS,
          );
          const tabToHide = currentTabs?.patientTabs.find(
            (tab) =>
              tab.patient.id === fromPatientId &&
              tab.tabType === PatientTabType.PREVIEW,
          );

          if (tabToHide) {
            setHiddenPreviewTabs((prev) => ({
              ...prev,
              [fromPatientId as string]: tabToHide,
            }));
            queryClient.setQueryData<PatientTabsResponse>(
              QueryKeys.Provider.PATIENT_TABS,
              (oldData) => ({
                ...(oldData || { patientTabs: [] }),
                patientTabs:
                  oldData?.patientTabs.filter(
                    (tab) =>
                      tab.patient.id !== fromPatientId ||
                      tab.tabType !== PatientTabType.PREVIEW,
                  ) || [],
              }),
            );
          }
        }

        // Handle navigating to a route that might have a hidden Preview tab
        if (toPatientId) {
          const tabToRestoreData = hiddenPreviewTabsRef.current[toPatientId];
          if (tabToRestoreData) {
            const currentTabs = queryClient.getQueryData<PatientTabsResponse>(
              QueryKeys.Provider.PATIENT_TABS,
            );
            // Add back only if it's not already there (e.g. some other process added it)
            const isAlreadyPresent = currentTabs?.patientTabs.some(
              (tab) => tab.patient.id === toPatientId,
            );
            if (!isAlreadyPresent) {
              queryClient.setQueryData<PatientTabsResponse>(
                QueryKeys.Provider.PATIENT_TABS,
                (oldData) => ({
                  ...(oldData || { patientTabs: [] }),
                  patientTabs: [
                    tabToRestoreData,
                    ...(oldData?.patientTabs || []),
                  ],
                }),
              );
            }
            setHiddenPreviewTabs((prev) => {
              const newState = { ...prev };
              delete newState[toPatientId as string];
              return newState;
            });
          }
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [router, queryClient]);
};
